import { sortParam } from '../sort.js';
import { z } from 'zod';
import { paginationQuery } from '../api.js';
import { nullableDate, nullableText, optionalDate, optionalText } from '../common.js';
import { TAX_RATES } from './calc.js';
/**
 * Verträge des Moduls Angebote (KON-034). Firmendaten, Angebote mit Positionen, Versand und Online-Annahme. Den Katalog stellt seit KON-113 das Modul Produkte. Beträge sind ganze Cent,
 * Mengen Tausendstel, Rabatte Basispunkte, siehe calc.ts.
 */
const taxRate = z.union(TAX_RATES.map((r) => z.literal(r)), 'Steuersatz 19, 7 oder 0 %');
const cents = z.number().int('Betrag in Cent').min(-1_000_000_000).max(1_000_000_000);
const basisPoints = z.number().int().min(0, 'Rabatt mindestens 0 %').max(10000, 'Rabatt höchstens 100 %');
const unit = z.string().trim().min(1, 'Einheit fehlt').max(20);
// ---------------------------------------------------------------------------
// Firmendaten (KON-097)
// ---------------------------------------------------------------------------
const text = (max) => z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));
export const companyProfileInput = z
    .object({
    companyName: text(160),
    street: text(120),
    postalCode: text(10),
    city: text(80),
    phone: text(40),
    email: z.string().trim().max(200).optional().transform((v) => (v ? v : undefined)).pipe(z.email('Ungültige E-Mail-Adresse').optional()),
    website: text(200),
    managingDirector: text(160),
    registerCourt: text(120),
    registerNumber: text(60),
    taxNumber: text(40),
    vatId: z.string().trim().max(20).optional().transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : undefined)).pipe(z.string().regex(/^[A-Z]{2}[A-Z0-9]{2,13}$/, 'Ungültige USt-IdNr., zum Beispiel DE123456789').optional()),
    bankName: text(120),
    iban: z.string().trim().max(42).optional().transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : undefined)).pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/, 'Ungültige IBAN, zum Beispiel DE89 3704 0044 0532 0130 00').optional()),
    bic: z.string().trim().max(11).optional().transform((v) => (v ? v.replace(/\s+/g, '').toUpperCase() : undefined)).pipe(z.string().regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'Eine BIC hat 8 oder 11 Zeichen, zum Beispiel COBADEFFXXX').optional()),
    footerNote: text(500),
    /** Steuerberater und DATEV (KON-139): Name und E-Mail der Kanzlei, Beraternummer (bis 7 Ziffern) und DATEV-Mandantennummer (bis 5 Ziffern) */
    taxAdvisorName: text(160),
    taxAdvisorEmail: z.string().trim().max(200).optional().transform((v) => (v ? v : undefined)).pipe(z.email('Ungültige E-Mail-Adresse').optional()),
    datevConsultantNumber: z.string().trim().optional().transform((v) => (v ? v : undefined)).pipe(z.string().regex(/^\d{1,7}$/, 'Die Beraternummer hat bis zu 7 Ziffern').optional()),
    datevClientNumber: z.string().trim().optional().transform((v) => (v ? v : undefined)).pipe(z.string().regex(/^\d{1,5}$/, 'Die DATEV-Mandantennummer hat bis zu 5 Ziffern').optional()),
    /** Bundesland für die Feiertage beim Urlaub (KON-120), Vorgabe Bayern */
    state: z.enum(['BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH']).optional(),
    /** Kleinunternehmer nach § 19 UStG: keine Steuer im Angebot, mit Hinweis */
    smallBusiness: z.boolean().default(false),
    /** So viele Tage gilt ein neues Angebot */
    offerValidityDays: z.number().int().min(1).max(365).default(30),
    offerIntro: text(5000),
    offerClosing: text(5000),
    mailSubject: text(200),
    mailBody: text(5000),
})
    .strict();
/** Vorgaben, wenn der Mandant noch nichts eingetragen hat */
export const DEFAULT_OFFER_TEXTS = {
    intro: 'vielen Dank für Ihre Anfrage. Gern unterbreiten wir Ihnen folgendes Angebot:',
    closing: 'Wir freuen uns auf Ihren Auftrag. Bei Fragen erreichen Sie uns jederzeit.',
    mailSubject: 'Ihr Angebot {nummer}',
    mailBody: 'Guten Tag {name},\n\nanbei erhalten Sie unser Angebot {nummer}. Sie können es auch online ansehen und annehmen:\n{link}\n\nMit freundlichen Grüßen\n{firma}',
};
// ---------------------------------------------------------------------------
// Angebote (KON-098, KON-099)
// ---------------------------------------------------------------------------
export const OFFER_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired', 'superseded'];
export const OFFER_STATUS_LABELS = {
    draft: 'Entwurf',
    sent: 'Versendet',
    accepted: 'Angenommen',
    rejected: 'Abgelehnt',
    expired: 'Abgelaufen',
    superseded: 'Ersetzt',
};
export const offerLineInput = z.object({
    /** Herkunft aus den Produkten (KON-113), freiwillig */
    itemId: z.uuid('Ungültiges Produkt').nullable().optional(),
    title: z.string().trim().min(1, 'Bezeichnung fehlt').max(200),
    description: z.string().trim().max(2000).nullable().optional().transform((v) => (v ? v : null)),
    quantityMilli: z.number().int('Höchstens drei Nachkommastellen').min(1, 'Menge muss größer als 0 sein').max(100_000_000, 'Menge zu groß'),
    unit,
    unitPriceCents: cents,
    discountBp: basisPoints.default(0),
    taxRate,
});
const lines = z.array(offerLineInput).max(200, 'Höchstens 200 Positionen');
export const createOfferInput = z
    .object({
    contactId: z.uuid('Bitte einen Empfänger wählen'),
    inquiryId: z.uuid().optional(),
    subject: z.string().trim().min(2, 'Betreff fehlt').max(200),
    introText: optionalText(5000),
    closingText: optionalText(5000),
    validUntil: optionalDate,
    discountBp: basisPoints.default(0),
    lines: lines.default([]),
})
    .strict();
export const updateOfferInput = z
    .object({
    contactId: z.uuid('Bitte einen Empfänger wählen').optional(),
    inquiryId: z.uuid().nullable().optional(),
    subject: z.string().trim().min(2, 'Betreff fehlt').max(200).optional(),
    introText: nullableText(5000),
    closingText: nullableText(5000),
    validUntil: nullableDate,
    discountBp: basisPoints.optional(),
    /** Ersetzt alle Positionen */
    lines: lines.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listOffersQuery = paginationQuery.extend({
    q: z.string().trim().max(100).optional(),
    status: z.enum(OFFER_STATUSES).optional(),
    contactId: z.uuid().optional(),
    inquiryId: z.uuid().optional(),
    sort: sortParam(['number', 'contact', 'status', 'validUntil', 'gross', 'createdAt'], '-createdAt'),
});
/** Entscheidung des Büros: angenommen oder abgelehnt, mit freiwilliger Notiz */
export const decideOfferInput = z.object({ decision: z.enum(['accepted', 'rejected']), note: optionalText(1000) }).strict();
/** Anzeige des Status mit „Manuell versendet“ für Angebote, die ohne E-Mail aus Wirkia abgeschlossen wurden (KON-114) */
export function offerStatusLabel(status, sentVia) {
    return status === 'sent' && sentVia === 'manual' ? 'Manuell versendet' : OFFER_STATUS_LABELS[status];
}
// ---------------------------------------------------------------------------
// Versand (KON-101) und Online-Annahme (KON-102)
// ---------------------------------------------------------------------------
export const sendOfferInput = z
    .object({
    to: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
    subject: z.string().trim().min(2, 'Betreff fehlt').max(200),
    body: z.string().trim().min(2, 'Text fehlt').max(10000),
    /** Den Link zur Online-Annahme mitschicken (ersetzt einen früheren Link) */
    includeLink: z.boolean().default(true),
})
    .strict();
export const publicAcceptInput = z.object({
    name: z.string().trim().min(2, 'Bitte geben Sie Ihren Namen an').max(160),
    confirm: z.literal(true, 'Bitte bestätigen Sie die Annahme'),
});
export const publicRejectInput = z.object({ reason: optionalText(1000) });
//# sourceMappingURL=offers.js.map